import { FormHelperText, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { FormControl } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ButtonForm } from "../../button-form";
import { useEffect, useMemo, useState } from "react";
import { Branch, ManagerPaycloud } from "../../../../types";
import { useToast } from "../../../../context/toast-context";
import { useAuth } from "../../../../context/auth-context";
import { BranchService } from "../../../../services/branch-service";
import { ManagerService } from "../../../../services/manager-service";

interface EditManagerFormDOProps {
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  manager: ManagerPaycloud;
  fetchManagers?: () => Promise<void>;
}

// Parsear el nombre completo si no vienen firstName/lastName separados
const parseFullName = (fullName: string) => {
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');
  return { firstName, lastName };
};

/**
 * Formulario de edición de manager para República Dominicana
 * Los managers de DO usan Cédula en lugar de CUIL
 * NO requieren provincia ni ciudad
 */
export function EditManagerFormDO({
  setIsModalOpen,
  manager,
  fetchManagers,
}: EditManagerFormDOProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  
  const { firstName: parsedFirstName, lastName: parsedLastName } = 
    manager.firstName ? { firstName: manager.firstName, lastName: manager.lastName || '' } : parseFullName(manager.name || '');

  const [formData, setFormData] = useState({
    ...manager,
    firstName: parsedFirstName,
    lastName: parsedLastName,
    identityNumber: manager.identityNumber || '',
    phone: manager.phone || '',
    address: manager.address || '',
    departmentId: 0, // No requerido para DO
    cityId: 0, // No requerido para DO
  });
  const { showToast } = useToast();
  const { country } = useAuth();
  const managerService = useMemo(() => new ManagerService(), []);
  const branchService = useMemo(() => new BranchService(), []);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>(manager.branches.map(b => b.branchId));
  const [errors, setErrors] = useState({
      firstName: "",
      lastName: "",
      email: "",
      cedula: "",
      address: "",
      phone: "",
      branchesIds: "",
    });

  // Sincronizar formData cuando cambia el manager
  useEffect(() => {
    const { firstName: newFirstName, lastName: newLastName } = 
      manager.firstName ? { firstName: manager.firstName, lastName: manager.lastName || '' } : parseFullName(manager.name || '');
    
    setFormData({
      ...manager,
      firstName: newFirstName,
      lastName: newLastName,
      identityNumber: manager.identityNumber || '',
      phone: manager.phone || '',
      address: manager.address || '',
      departmentId: 0,
      cityId: 0,
    });
  }, [manager]);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        await branchService.getBranches(
          { limit: 100, offset: 0 },
          (data: any) => {
            setBranches(data.branches);
          },
          (errorMessage: any) => {
            showToast(errorMessage, "error");
          }
        );
      } catch (error) {
        showToast(t("common.errors.generic"), "error");
      }
    };
  
      loadBranches();
    }, [branchService, showToast, t]);

  // Sincronizar sucursales seleccionadas cuando se carguen las branches
  useEffect(() => {
    if (branches.length > 0 && manager.branches && manager.branches.length > 0) {
      const managerBranchIds = manager.branches.map(b => b.branchId);
      setSelectedBranches(managerBranchIds);
      setFormData(prev => ({
        ...prev,
        branches: manager.branches
      }));
    }
  }, [branches, manager.branches]);

    const validate = () => {
      let isValid = true;
      const newErrors = {
        firstName: "",
        lastName: "",
        email: "",
        cedula: "",
        address: "",
        phone: "",
        branchesIds: "",
      };
  
      if (!formData.firstName) {
        newErrors.firstName = t("managers.create.errors.name");
        isValid = false;
      }
  
      if (!formData.lastName) {
        newErrors.lastName = t("managers.create.errors.name");
        isValid = false;
      }
  
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t("managers.create.errors.email");
        isValid = false;
      }
  
      // Validación de cédula dominicana: debe tener entre 8 y 11 dígitos
      if (!formData.identityNumber) {
        newErrors.cedula = t("managers.create.errors.cedula");
        isValid = false;
      } else if (formData.identityNumber.length < 8 || formData.identityNumber.length > 11) {
        newErrors.cedula = t("managers.create.errors.cedula-length");
        isValid = false;
      } else if (!/^\d+$/.test(formData.identityNumber)) {
        newErrors.cedula = t("managers.create.errors.cedula-format");
        isValid = false;
      }
  
      if (!formData.address) {
        newErrors.address = t("managers.create.errors.address");
        isValid = false;
      } else if (formData.address.length < 5) {
        newErrors.address = t("managers.create.errors.address-min");
        isValid = false;
      } else if (formData.address.length > 50) {
        newErrors.address = t("managers.create.errors.address-max");
        isValid = false;
      }
  
      if (!formData.phone) {
        newErrors.phone = t("managers.create.errors.phone-required");
        isValid = false;
      } else if (formData.phone.length < 7 || formData.phone.length > 20) {
      newErrors.phone = t("managers.create.errors.phone-size");
      isValid = false;
    }
  
      if (formData.branches.length === 0) {
        newErrors.branchesIds = t("managers.create.errors.branchesIds");
        isValid = false;
      }
  
      setErrors(newErrors);
      return isValid;
    };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev: typeof formData) => ({ ...prev, [name]: value }));
    };

const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();
  setLoading(true);

  if (!validate()) {
    setLoading(false);
    return;
  }

  try {
    await managerService.updateManager(
      manager.userId,
      {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        Email: manager.email ?? formData.email,
        IdentityType: "CEDULA",
        IdentityNumber: formData.identityNumber,
        Address: formData.address,
        Phone: formData.phone,
        DepartmentId: "0", // No requerido para DO
        CityId: "0", // No requerido para DO
        BranchesIds: formData.branches.map(b => b.branchId),
        CountryCode: country ?? "DO",
      },
      async () => {
        setIsModalOpen && setIsModalOpen(false);
        showToast(t("managers.edit-success"), "success");
        if (fetchManagers) {
          await fetchManagers();
        }
      },
      (errorMessage : string) => {
        showToast(errorMessage, "error");
      }
    );
  } finally {
    setLoading(false);
  }
};

const handleBranchChange = (event: SelectChangeEvent<number[]>) => {
  const selectedOptions = event.target.value as number[];
  setSelectedBranches(selectedOptions);
  const selectedBranches = branches.filter(b => selectedOptions.includes(b.branchId));
  setFormData((prev: typeof formData) => ({ 
    ...prev, 
    branches: selectedBranches
  }));
};

const textFieldStyles = {
  "& .MuiInput-root": {
    color: "#000",
    fontFamily: "Montserrat",
    "&:before": {
      borderBottom: "1px solid #838383",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: "#898989",
    },
  },
  "& label.Mui-focused": {
    color: "#7e7e7e",
  },
  "& .MuiInputLabel-standard": {
    color: "#000",
    fontWeight: "400",
    fontFamily: "Montserrat",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#838383",
  },
};

  return (
  <form onSubmit={handleSubmit} className="">
    <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
      <h1>{t("managers.edit-title")}</h1>
    </div>
    <div className="grid gap-4 p-6">
      <TextField
        type="text"
        name="firstName"
        error={!!errors.firstName}
        helperText={errors.firstName}
        label={t("managers.create.name")}
        value={formData.firstName || ""}
        onChange={handleChange}
        variant="standard"
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="lastName"
        error={!!errors.lastName}
        helperText={errors.lastName}
        label={t("managers.create.lastName")}
        value={formData.lastName || ""}
        onChange={handleChange}
        variant="standard"
        sx={textFieldStyles}
      />

      <TextField
        type="email"
        name="email"
        error={!!errors.email}
        helperText={errors.email}
        label={t("managers.create.email")}
        value={formData.email || ""}
        onChange={handleChange}
        disabled
        variant="standard"
        sx={textFieldStyles}
      />

      {/* En República Dominicana usan Cédula en lugar de CUIL */}
      <TextField
        type="text"
        name="identityNumber"
        error={!!errors.cedula}
        helperText={errors.cedula}
        label={t("managers.create.cedula", "Cédula")}
        value={formData.identityNumber || ""}
        onChange={handleChange}
        onInput={(e) => {
          const input = e.target as HTMLInputElement;
          let value = input.value;
          value = value.replace(/[^\d]/g, "");
          // Permitir hasta 11 dígitos para República Dominicana
          if (value.length > 11) {
            value = value.substring(0, 11);
          }
          input.value = value;
          setFormData(prev => ({ ...prev, identityNumber: value }));
        }}
        variant="standard"
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="address"
        error={!!errors.address}
        helperText={errors.address}
        label={t("managers.create.address")}
        value={formData.address || ""}
        onChange={handleChange}
        variant="standard"
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="phone"
        error={!!errors.phone}
        helperText={errors.phone}
        label={t("managers.create.phone")}
        value={formData.phone || ""}
        onChange={handleChange}
        onInput={(e) => {
          const input = e.target as HTMLInputElement;
          let value = input.value;
          value = value.replace(/[^\d]/g, "");
          if (value.length > 10) {
            value = value.substring(0, 10);
          }
          input.value = value;
          setFormData(prev => ({ ...prev, phone: value }));
        }}
        variant="standard"
        sx={textFieldStyles}
      />

      {/* República Dominicana NO requiere provincia ni ciudad */}

      <FormControl error={!!errors.branchesIds} variant="standard" fullWidth>
        <Select
          sx={{
            paddingX: 2,
            paddingY: 1,
            margin: 0,
            fontStyle: "normal",
            fontFamily: "Montserrat",
            fontSize: "1rem",
            height: "100%",
            width: "100%",
            borderRadius: "9999px",
            border: "1px solid",
            borderColor: "#838383",
            backgroundColor: "transparent",
            outline: "none",
            "&:focus": {
              outlineColor: "primary.main",
            },
            "& .MuiSelect-select": {
              padding: 0,
              display: "block",
              minWidth: 0,
            },
            "&.MuiInputBase-root.MuiOutlinedInput-root": {
              padding: "4px 8px",
            },
            "& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
              {
                WebkitTextFillColor: "#838383",
                opacity: "70%",
              },
            "& .MuiSvgIcon-root.MuiSelect-icon": {
              color: "white",
            },
            color: "#000",
            fontWeight: "400",
            "&:before": {
              borderBottom: "none"
            },
            "&:after": {
              borderBottom: "none"
            },
            "&:hover:not(.Mui-disabled):before": {
              borderBottom: "none"
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "white",
                color: "#000",
                "& .MuiMenuItem-root": {
                  "&:hover": {
                    backgroundColor: "#f5f5f5"
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#e0e0e0",
                    "&:hover": {
                      backgroundColor: "#d5d5d5"
                    }
                  }
                }
              },
            },
          }}
          multiple
          name="BranchesIds"
          value={selectedBranches}
          onChange={handleBranchChange}
          displayEmpty
          renderValue={(selected) => {
            if (selected.length === 0) {
              return (
                <span className="block truncate">
                  {t("managers.branchesIds")}
                </span>
              );
            }
            return selected
              .map((id) => branches.find((b) => b.branchId === id)?.name)
              .join(", ");
          }}
        >
          <MenuItem
            sx={{
              fontFamily: "Montserrat",
            }}
            disabled
            value=""
          >
            <span>{t("managers.branchesIds")}</span>              
          </MenuItem>
          {branches.map((branch) => (
            <MenuItem
              sx={{
                fontFamily: "Montserrat",
              }}
              key={`branch-${branch.branchId}`}
              value={branch.branchId}
            >
              {branch.name}
            </MenuItem>
          ))}
        </Select>
        {errors.branchesIds && (
          <FormHelperText>{errors.branchesIds}</FormHelperText>
        )}
      </FormControl>

      <div className="w-full flex flex-col gap-4">
        <ButtonForm loading={loading}>
          {t("managers.edit-submit")}
        </ButtonForm>
      </div>
    </div>
  </form>
  
  );
}

